import moment from 'moment';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { fetchPackageWelcome, fetchUpdatePackageProgress } from '../../actions/EnrollmentActions';
import { ContentCard } from '../Cards';
import { Link } from 'react-router-dom';
import '../../styles/components.scss';
import { userService } from '../../utils/UserService';
import { stepNumberHelper } from '../../utils/StepNumberHelper';
import { FaAngleDoubleRight } from 'react-icons/fa';
import { fetchDivisions } from '../../actions/DivisionActions';
import { updatePackageEnrollmentAssistantData, fetchPackageInfo } from '../../actions/PackageActions';
import { getPersonNameFirst } from '../../utils/NameHelper';
import { Package, PackageStepType } from '../../types/api/enrollmentPackages/Package';
import { EnrollmentAssistantModal } from '../Modals/EnrollmentAssistantModal';
import { permissionsService } from '../../utils/PermissionService';
import { UserRole } from '../../types/enums/UserRole';
import { EnrollmentPackageId, PersonId } from '../../types/api/primaryKeys/PrimaryKey';
import { useWolfTracsState } from '../../hooks/redux/useWolfTracsState';
import { useReduxDispatch } from '../../hooks/redux/useReduxDispatch';
import { DivisionDetail } from '../../types/api/divisions/DivisionDetail';
import { PackageMessageContent } from '../../types/api/enrollmentContent/PackageMessageContent';
import { EmployeeDetail } from '../../types/api/employees/EmployeeDetail';
import { useMounted } from '../../hooks/components/useMounted';
import { UserDetail } from '../../types/api/users/UserDetail';
import { Nullable } from '../../types/custom/Nullable';
import { EnrollmentAssistantFormData } from '../Modals/EnrollmentAssistantModal';
import { PackageType } from '../../types/enums/PackageType';

interface Props {
    packageId: EnrollmentPackageId;
}

const PackageWelcome: FC<Props> = ({ packageId }) => {
    const [ user, setUser ] = useState<Nullable<UserDetail>>(null);
    const [ person, setPerson ] = useState<Nullable<EmployeeDetail>>(null);

    const [ showEAModal, setShowEAModal ] = useState(false);
    const [ preventContinue, setPreventContinue ] = useState(true);
    const [ overrideEnrollmentAssistant, setOverrideEnrollmentAssistant ] = useState(false);
    const [ didUserSaveEnrollmentAssistantInfo, setDidUserSaveEnrollmentAssistantInfo ] = useState(false);

    const { data: welcomePage, loading: welcomeLoading } = useWolfTracsState(s => s.enrollment.welcomePage);
    const { data: activePackage } = useWolfTracsState(s => s.packages.activePackage);
    const { data: divisions, loading: divisionsLoading } = useWolfTracsState(s => s.divisions.personDivisions);

    const dispatch = useReduxDispatch();

    const isMounted = useMounted();

    // Loading of the "Package Welcome" data.
    // This can be run independently as it's only dependent on the current package id.
    useEffect(() => {
        dispatch(fetchPackageWelcome(packageId));
    }, [packageId]);

    // Fetches the current user on load, stores it in local state.
    useEffect(() => {
        async function fetchCurrentUser() {
            const currentUser = await userService.currentUser();
            if (isMounted) {
                setUser(currentUser);
            }
        }
        fetchCurrentUser();
    }, []);

    // Fetches the current person on load, stores it in local state.
    useEffect(() => {
        async function fetchCurrentPerson() {
            const currentPerson = await userService.currentPerson();
            if (isMounted) {
                setPerson(currentPerson);
            }
        }
        fetchCurrentPerson();
    }, []);


    useEffect(() => {
        // ToDo: There are more state vars not set up as dependencies!

        // check if user is enrollment assistant and then check if info is already associated with package
        // otherwise popup the enrollment modal
        const checkForEnrollmentAssistance = async (user: UserDetail, activePackage: Package) => {
            // Depends on [activePackage, user] ()

            const isCurrentUserAnEnrollmentAssistant = permissionsService.userHasOnlyThisRole(user, UserRole.Enrollment_Assistant);

            // code that handles showing modal when user is enrollment assistant, they should always see the modal
            if (isCurrentUserAnEnrollmentAssistant && !didUserSaveEnrollmentAssistantInfo && !overrideEnrollmentAssistant) {
                toggleEAModal(true);
                setPreventContinue(true);
            }


            // [ currentuser, activePackage, useEnrollmentAssistant]
            const isHarmonyPlanIncluded = activePackage.steps.some(s => s.harmonyProductGroup !== null);

            // in case an agent had saved info and there is an enrollment association tied to it for harmony
            // code to ensure there is no impersonation done in case, an employee or admin signs back in and enrollmentpackage agent info is saved
            // if useEnrollment setting is not set?
            if (!isCurrentUserAnEnrollmentAssistant && isHarmonyPlanIncluded && activePackage.enrollmentAssistantId && !divisionUsesEnrollmentAssistant) {
                const resetEnrollmentData = {
                    enrollmentAssistantId: null,
                    enrollmentAssistantName: null
                };

                await dispatch(updatePackageEnrollmentAssistantData(
                    activePackage.enrollmentPackageId,
                    resetEnrollmentData
                ));

                dispatch(fetchPackageInfo(packageId));

                if (showEAModal) {
                    setPreventContinue(false);
                }
            }
        };

        if (user && activePackage) {
            checkForEnrollmentAssistance(user, activePackage);
        }
    }, [user, activePackage]);


    useEffect(() => {
        function getPersonDivisions(personId: PersonId) {
            dispatch(fetchDivisions(personId));
        }

        if (person?.personId) {
            getPersonDivisions(person.personId);
        }
    }, [person?.personId]);

    const divisionUsesEnrollmentAssistant = useMemo(() => {
        const anyDivisionsWithSetting = divisions?.some(d => d.useEnrollmentAssistant) ?? false;
        return anyDivisionsWithSetting;
    }, [divisions]);

    useEffect(() => {
        const checkDivisionFlowState = (divisions: readonly DivisionDetail[], activePackage: Package) => {
            if (!divisions.some(d => d.useEnrollmentAssistant)) {
                setPreventContinue(false);
                return;
            }
        
            if (!activePackage.enrollmentAssistantId && !overrideEnrollmentAssistant) {
                toggleEAModal(true);
                setPreventContinue(true);
            } else {
                setPreventContinue(false);
            }
        };

        if (divisions && activePackage) {
            checkDivisionFlowState(divisions, activePackage);
        }
    }, [divisions, activePackage]);



    const onBeginEnrollment = (activePackage: Package) => {
        const currentStepNumber = stepNumberHelper.stepNumberForNamedStep(activePackage, PackageStepType.Welcome);
        dispatch(fetchUpdatePackageProgress(packageId, currentStepNumber, true));
    };

    const toggleEAModal = (newShowEAModalValue: boolean) => {
        // ToDo: Check if this is needed. I seriously doubt this does anything at all anymore. I'm scared if it does.
        // ToDo: Once this class is removed from the body, refactor calls to this method to just update show modal to the appropriate state...
        newShowEAModalValue ? document.body.classList.add('modal-open') : document.body.classList.remove('modal-open');

        setShowEAModal(newShowEAModalValue);
    };

    const onUpdateEnrollmentAssistant = async (formData: EnrollmentAssistantFormData) => {
        await dispatch(updatePackageEnrollmentAssistantData(packageId, formData));

        toggleEAModal(false);

        setPreventContinue(false);
        setDidUserSaveEnrollmentAssistantInfo(true);

        dispatch(fetchPackageInfo(packageId));
    };

    const onOverrideEnrollmentAssistant = (e: React.ChangeEvent<HTMLInputElement>) => {
        setOverrideEnrollmentAssistant(e.target.checked);
        setPreventContinue(activePackage?.enrollmentAssistantId ? false : true); //maybe base it off of checked mark?
    };

    const closeOverride = async () => {
        const resetEnrollmentData = {
            enrollmentAssistantId: null,
            enrollmentAssistantName: null
        };

        await dispatch(updatePackageEnrollmentAssistantData(
            packageId,
            resetEnrollmentData
        ));

        toggleEAModal(false);
        dispatch(fetchPackageInfo(packageId));
    };

    const renderModal = (activePackage: Package, welcomePage: PackageMessageContent, user: UserDetail) => {
        //welcomePage is always populated with data object similar with activePackage always having data populated
        const employeeInfo = {
            city: welcomePage.employeeCity,
            state: welcomePage.employeeState
        };

        return (
            <EnrollmentAssistantModal
                showModal={showEAModal}
                activePackage={activePackage}
                updateEnrollmentAssistant={onUpdateEnrollmentAssistant}
                overrideEnrollmentAssistant={overrideEnrollmentAssistant}
                setOverride={onOverrideEnrollmentAssistant}
                closeEAModal={() => toggleEAModal(false)}
                closeOverride={closeOverride}
                user={user}
                employeeInfo={employeeInfo}
            />
        );
    };

    // Main Render
    const employeeName = getPersonNameFirst(person);

    return (
        <ContentCard loading={welcomeLoading || divisionsLoading || !activePackage}>
            {activePackage && welcomePage &&
                <React.Fragment>
                    {user && renderModal(activePackage, welcomePage, user)}
                    <h2>Overview</h2>
                    
                    { /* This GTFF hardcoding is not ideal, but we have to speedily add some of these features.*/}
                    <p>Hello{activePackage.packageTypeId === PackageType.PublicEnrollment ? ' GTFF Employee' : employeeName ? " " + employeeName : ''},</p>

                    {welcomePage.content && <div dangerouslySetInnerHTML={{ __html: welcomePage.content }} />}

                    <p className="bold">This package will remain available until {moment(welcomePage.endDate).format("YYYY-MM-DD")}.</p>

                    <div className="tc">
                        <Link to={'employee'}>
                            {!overrideEnrollmentAssistant &&
                                <button className="btn btn-primary" onClick={() => onBeginEnrollment(activePackage)} disabled={preventContinue}>Begin Enrollment <FaAngleDoubleRight /></button>
                            }
                            {overrideEnrollmentAssistant &&
                                <button className="btn btn-primary" onClick={() => onBeginEnrollment(activePackage)}>Begin Enrollment <FaAngleDoubleRight /></button>
                            }
                        </Link>

                        <br />
                        {preventContinue && <button type="button" className="btn btn-warning mt-2" onClick={() => toggleEAModal(true)}>Enter Enrollment Assistant ID</button>}
                        {!preventContinue && (activePackage.enrollmentAssistantId || overrideEnrollmentAssistant) && <button type="button" className="btn btn-warning mt-2" onClick={() => toggleEAModal(true)}>View Enrollment Assistant ID</button>}
                    </div>
                </React.Fragment>
            }
        </ContentCard>
    );
};

export default PackageWelcome;
